import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Avatar,
  Grid,
  Divider,
  DialogContentText,
  IconButton
} from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DeleteIcon from '@mui/icons-material/Delete';
import * as mockDataService from '../services/mockData';

interface LeadProfileProps {
  lead: {
    id: string;
    name: string;
    email: string;
    phone: string;
    interestLevel: 'alto' | 'médio' | 'baixo';
    status: 'novo' | 'em_contato' | 'agendado' | 'fechado';
    notes: string;
    createdAt: string;
    lastContact: string;
  };
  open: boolean;
  onClose: () => void;
  onDelete?: (id: string) => void;
}

const LeadProfile: React.FC<LeadProfileProps> = ({ lead, open, onClose, onDelete }) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const getInterestLevelColor = (level: string) => {
    switch (level) {
      case 'alto':
        return 'success';
      case 'médio':
        return 'warning';
      case 'baixo':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'novo':
        return 'info';
      case 'em_contato':
        return 'warning';
      case 'agendado':
        return 'primary';
      case 'fechado':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      if (isValid(date)) {
        return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
      }
      return 'Data não disponível';
    } catch (error) {
      return 'Data não disponível';
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await mockDataService.deleteLead(lead.id);
      if (onDelete) {
        onDelete(lead.id);
      }
      handleCloseDeleteDialog();
      onClose();
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {lead.name.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h6">{lead.name}</Typography>
            </Box>
            <IconButton 
              color="error" 
              onClick={handleDeleteClick}
              aria-label="excluir lead"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box display="flex" gap={2} mb={2}>
                <Chip
                  label={`Interesse: ${lead.interestLevel}`}
                  color={getInterestLevelColor(lead.interestLevel)}
                />
                <Chip
                  label={`Status: ${lead.status.replace('_', ' ')}`}
                  color={getStatusColor(lead.status)}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">{lead.email}</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Telefone
              </Typography>
              <Typography variant="body1">{lead.phone}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Data de Cadastro
              </Typography>
              <Typography variant="body1">{formatDate(lead.createdAt)}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Último Contato
              </Typography>
              <Typography variant="body1">{formatDate(lead.lastContact)}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Observações
              </Typography>
              <Typography variant="body1">{lead.notes}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
          <Button variant="contained" color="primary">
            Editar Lead
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirmar exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Tem certeza que deseja excluir o lead <strong>{lead.name}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained" 
            autoFocus
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LeadProfile; 